// src/components/LinearWeekViewDOM.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { HiChevronRight } from 'react-icons/hi2';
import LinearDayColumnDOM from './LinearDayColumnDOM'; // Component for a single day column
import Card from './ui/Card'; // Optional: Wrap in a card
import { SCHEDULE_START_MINUTE, SCHEDULE_END_MINUTE, TOTAL_SCHEDULE_MINUTES } from '../config/constants';
import { formatTimeFromMinutes, getWeekStartDate } from '../utils/helpers.ts';

/** Desktop: comfortable gutter. Mobile: tight — HH:MM at text-xs fits without clipping. */
const TIME_COLUMN_WIDTH_DESKTOP = 80;
const TIME_COLUMN_WIDTH_MOBILE = 44;

function getViewportWidth() {
  if (typeof window === 'undefined') return 1024;
  return window.innerWidth;
}

function getTimeColumnWidthForViewport(vw) {
  return vw < 768 ? TIME_COLUMN_WIDTH_MOBILE : TIME_COLUMN_WIDTH_DESKTOP;
}

function getDefaultColumnWidth(numCols, timeColumnWidth = TIME_COLUMN_WIDTH_DESKTOP) {
  const vw = getViewportWidth();
  // On mobile, default to fitting available days in viewport (minus the time column)
  if (vw < 768) {
    const available = Math.max(0, vw - timeColumnWidth);
    return Math.max(available / 3, vw / Math.max(1, numCols - 2));
  }
  return 160;
}

const EDGE_NEXT_WEEK_THRESHOLD_PX = 56;
const EDGE_NEXT_WEEK_MAX_PX = 120;

function LinearWeekViewDOM({
  items,
  days,
  onOpenModal,
  user,
  currentWeekStart,
  selectedTeacher,
  selectedGroups,
  onNextWeek,
}) {
  const [timeColumnWidth, setTimeColumnWidth] = useState(() =>
    typeof window !== 'undefined' ? getTimeColumnWidthForViewport(window.innerWidth) : TIME_COLUMN_WIDTH_DESKTOP
  );

  // Calculate week start date for day numbering
  const weekStartDate = currentWeekStart || getWeekStartDate(new Date());

  // Generate time markers for the left side
  const timeMarkers = useMemo(() => {
    const markers = [];
    const interval = 60; // Show marker every hour

    for (let minutes = SCHEDULE_START_MINUTE; minutes <= SCHEDULE_END_MINUTE; minutes += interval) {
      if (minutes % 60 === 0) {
        const topPercent = Math.min(100, ((minutes - SCHEDULE_START_MINUTE) / TOTAL_SCHEDULE_MINUTES) * 100);
        markers.push({
          time: formatTimeFromMinutes(minutes),
          style: { top: `${topPercent}%` }
        });
      }
    }
    return markers;
  }, []);

  // Helper function to detect overlapping items within the same day
  const getOverlappingGroups = useMemo(() => {
    const groups = [];

    // Group items by day first
    const itemsByDay = {};
    items.forEach(item => {
      if (!item || !item.day) return;
      if (!itemsByDay[item.day]) itemsByDay[item.day] = [];
      itemsByDay[item.day].push(item);
    });

    // For each day, find overlapping groups
    Object.keys(itemsByDay).forEach(day => {
      const dayItems = itemsByDay[day];

      for (const entry of dayItems) {
        if (!entry || !entry.startMinutes || !entry.durationMinutes) continue;

        // Find if this entry overlaps with any existing group
        let addedToGroup = false;

        for (const group of groups) {
          // Check if this entry overlaps with any item in this group
          const overlapsWithGroup = group.some(groupEntry => {
            return (
              groupEntry.day === entry.day &&
              groupEntry.startMinutes !== null &&
              groupEntry.durationMinutes !== null &&
              entry.startMinutes !== null &&
              entry.durationMinutes !== null &&
              // Check time overlap: two items overlap if one starts before the other ends
              groupEntry.startMinutes < (entry.startMinutes + entry.durationMinutes) &&
              entry.startMinutes < (groupEntry.startMinutes + groupEntry.durationMinutes)
            );
          });

          if (overlapsWithGroup) {
            group.push(entry);
            addedToGroup = true;
            break;
          }
        }

        // If not added to any existing group, create a new group
        if (!addedToGroup) {
          groups.push([entry]);
        }
      }
    });

    return groups;
  }, [items]);

  // Determine dynamic grid columns based on number of days
  const numCols = Array.isArray(days) ? days.length : 5;
  const gridColsClass = (() => {
    switch (numCols) {
      case 1: return 'grid-cols-1';
      case 2: return 'grid-cols-2';
      case 3: return 'grid-cols-3';
      case 4: return 'grid-cols-4';
      case 5: return 'grid-cols-5';
      case 6: return 'grid-cols-6';
      default: return 'grid-cols-7';
    }
  })();

  // Pinch-to-zoom column width (mobile). When null, we fall back to the legacy grid layout.
  const [columnWidth, setColumnWidth] = useState(() => {
    if (typeof window === 'undefined') return null;
    const vw = window.innerWidth;
    return vw < 768 ? getDefaultColumnWidth(numCols, getTimeColumnWidthForViewport(vw)) : null;
  });

  const scrollContainerRef = useRef(null);
  const pinchStateRef = useRef({ initialDistance: 0, initialWidth: 0, midpointX: 0, initialScrollLeft: 0 });
  const edgePullLastXRef = useRef(0);
  const edgePullLiveRef = useRef(0);

  const [edgePullPx, setEdgePullPx] = useState(0);
  const [edgePullTouching, setEdgePullTouching] = useState(false);

  const scheduleWeekKey =
    currentWeekStart instanceof Date
      ? currentWeekStart.getTime()
      : currentWeekStart
        ? String(currentWeekStart)
        : '';

  useEffect(() => {
    const node = scrollContainerRef.current;
    if (node) node.scrollLeft = 0;
  }, [scheduleWeekKey]);

  // Keep mobile/desktop behavior in sync with viewport resizes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => {
      const vw = window.innerWidth;
      const isMobile = vw < 768;
      const tcw = getTimeColumnWidthForViewport(vw);
      setTimeColumnWidth(tcw);
      setColumnWidth(prev => {
        if (!isMobile) return null;
        const minW = vw / 7;
        const maxW = vw;
        const next = prev == null ? getDefaultColumnWidth(numCols, tcw) : prev;
        return Math.min(maxW, Math.max(minW, next));
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [numCols]);

  // Pinch-to-zoom + mobile edge pull for "next week" (at max horizontal scroll).
  useEffect(() => {
    const node = scrollContainerRef.current;
    if (!node) return;

    const isMobile = columnWidth != null;

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        edgePullLiveRef.current = 0;
        setEdgePullPx(0);
        setEdgePullTouching(false);
        const rect = node.getBoundingClientRect();
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
        pinchStateRef.current = {
          initialDistance: getDistance(e.touches),
          initialWidth: columnWidth ?? getDefaultColumnWidth(numCols, timeColumnWidth),
          midpointX: midX,
          initialScrollLeft: node.scrollLeft,
        };
      } else if (e.touches.length === 1 && isMobile && onNextWeek) {
        edgePullLastXRef.current = e.touches[0].clientX;
        setEdgePullTouching(true);
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 2 && pinchStateRef.current.initialDistance > 0) {
        e.preventDefault();
        const dist = getDistance(e.touches);
        const ratio = dist / pinchStateRef.current.initialDistance;
        const vw = window.innerWidth;
        const minW = vw / 7;
        const maxW = vw;
        const newWidth = Math.min(maxW, Math.max(minW, pinchStateRef.current.initialWidth * ratio));

        setColumnWidth(newWidth);

        const { midpointX, initialScrollLeft, initialWidth } = pinchStateRef.current;
        const contentPointAtStart = initialScrollLeft + midpointX - timeColumnWidth;
        const scaleChange = newWidth / initialWidth;
        const newContentPoint = contentPointAtStart * scaleChange;
        const newScrollLeft = newContentPoint - (midpointX - timeColumnWidth);
        node.scrollLeft = Math.max(0, newScrollLeft);
        return;
      }

      if (e.touches.length === 1 && isMobile && onNextWeek && pinchStateRef.current.initialDistance === 0) {
        const maxScroll = Math.max(0, node.scrollWidth - node.clientWidth);
        const atRightEdge = maxScroll <= 8 || node.scrollLeft >= maxScroll - 8;
        const x = e.touches[0].clientX;
        const dx = x - edgePullLastXRef.current;
        edgePullLastXRef.current = x;

        if (!atRightEdge) {
          if (edgePullLiveRef.current > 0) {
            edgePullLiveRef.current = 0;
            setEdgePullPx(0);
          }
          return;
        }

        let pull = edgePullLiveRef.current;
        if (dx < 0) {
          pull = Math.min(EDGE_NEXT_WEEK_MAX_PX, pull - dx * 0.92);
          if (pull > 2) {
            e.preventDefault();
          }
        } else if (dx > 0) {
          pull = Math.max(0, pull - dx);
        }

        edgePullLiveRef.current = pull;
        setEdgePullPx(pull);
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2) {
        pinchStateRef.current.initialDistance = 0;
      }
      if (e.touches.length === 0 && isMobile && onNextWeek) {
        const p = edgePullLiveRef.current;
        if (p >= EDGE_NEXT_WEEK_THRESHOLD_PX) {
          onNextWeek();
        }
        edgePullLiveRef.current = 0;
        setEdgePullPx(0);
        setEdgePullTouching(false);
      }
    };

    node.addEventListener('touchstart', onTouchStart, { passive: true });
    node.addEventListener('touchmove', onTouchMove, { passive: false });
    node.addEventListener('touchend', onTouchEnd, { passive: true });
    node.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
      node.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [columnWidth, numCols, timeColumnWidth, onNextWeek]);

  const useFixedColumnWidth = columnWidth != null;
  const isMobileView = useFixedColumnWidth;
  const headerHeight = isMobileView ? 40 : 68;
  const scheduleHeight = isMobileView ? 'calc(100dvh - 70px)' : '800px';
  const markersHeight = isMobileView ? `calc(100dvh - 70px - ${headerHeight}px)` : '730px';
  const minColumnWidthPx = 160; // keep similar to previous 800px for 5 cols
  const dynamicMinWidthPx = useFixedColumnWidth
    ? numCols * columnWidth
    : numCols * minColumnWidthPx;
  const edgePullHintWidth = Math.min(
    EDGE_NEXT_WEEK_MAX_PX,
    Math.max(76, edgePullPx)
  );

  return (
    <Card className="relative overflow-hidden shadow-lg border-2 border-gray-200">
      {/* Header with enhanced styling */}


      {/* Main schedule container with horizontal scroll */}
      <div
        ref={scrollContainerRef}
        className="relative bg-gray-50 overflow-x-auto overflow-y-hidden md:overflow-y-auto"
        style={useFixedColumnWidth ? { touchAction: 'pan-x pan-y' } : undefined}
      >
        <div
          className={`flex ${edgePullTouching ? '' : 'transition-transform duration-200 ease-out'}`}
          style={{
            minWidth: `${timeColumnWidth + dynamicMinWidthPx}px`,
            ...(edgePullPx > 0 ? { transform: `translateX(-${edgePullPx}px)` } : {}),
          }}
        >
          {/* Time markers column — narrow on mobile to maximize day columns */}
          <div
            className="flex-shrink-0 bg-white border-r-2 border-gray-300 relative"
            style={{ width: `${timeColumnWidth}px`, height: scheduleHeight }}
          >
            {/* Time column header - removed "Time" text */}
            <div className="bg-gradient-to-b from-white to-gray-100 border-b-2 border-gray-300 h-[40px] md:h-[68px] flex items-center justify-center">
            </div>

            {/* Time markers */}
            <div className="relative" style={{ height: markersHeight }}>
              {/* Horizontal lines */}
              {timeMarkers.map(marker => (
                <div
                  key={`line-${marker.time}`}
                  className="absolute left-0 right-0 border-t border-gray-200"
                  style={marker.style}
                />
              ))}

              {/* Time labels positioned just below the lines */}
              {timeMarkers.map(marker => (
                <div
                  key={marker.time}
                  className="absolute left-0 right-0 flex justify-center"
                  style={{
                    top: `calc(${marker.style.top} + 4px)` // Position just below the line
                  }}
                >
                  <span className="text-xs font-medium text-gray-600 bg-white px-1 py-0.5 rounded-sm">
                    {marker.time}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Days grid container */}
          <div
            className={`flex-1 grid ${useFixedColumnWidth ? '' : gridColsClass}`}
            style={
              useFixedColumnWidth
                ? {
                  gridTemplateColumns: `repeat(${numCols}, ${columnWidth}px)`,
                  minWidth: `${dynamicMinWidthPx}px`,
                }
                : { minWidth: `${dynamicMinWidthPx}px` }
            }
          >
            {days.map(day => {
              const itemsForDay = items.filter(item => item && item.day === day);
              return (
                <LinearDayColumnDOM
                  key={day}
                  day={day}
                  items={itemsForDay}
                  onOpenModal={onOpenModal}
                  user={user}
                  weekStartDate={weekStartDate}
                  selectedTeacher={selectedTeacher}
                  selectedGroups={selectedGroups}
                  overlappingGroups={getOverlappingGroups}
                  columnHeight={scheduleHeight}
                />
              );
            })}
          </div>
        </div>

        {/* Subtle background grid for better visual alignment - hidden on mobile */}
        <div className="hidden md:block absolute inset-0 pointer-events-none" style={{ left: `${timeColumnWidth}px` }}>
          {/* Vertical day separators */}
          <div
            className={`grid ${useFixedColumnWidth ? '' : gridColsClass} h-full`}
            style={useFixedColumnWidth ? { gridTemplateColumns: `repeat(${numCols}, ${columnWidth}px)` } : undefined}
          >
            {days.map((day, index) => (
              <div
                key={`bg-${day}`}
                className={`border-r border-gray-100 ${index === days.length - 1 ? 'border-r-0' : ''}`}
              />
            ))}
          </div>

          {/* Horizontal time grid lines */}
          <div className="absolute inset-0" style={{ top: `${headerHeight}px` }}>
            {timeMarkers.map(marker => (
              <div
                key={`grid-line-${marker.time}`}
                className="absolute left-0 right-0 border-t border-gray-100"
                style={marker.style}
              />
            ))}
          </div>
        </div>
      </div>

      {useFixedColumnWidth && onNextWeek && edgePullPx > 4 && (
        <div
          className="absolute top-0 right-0 bottom-0 z-50 flex flex-col items-center justify-center gap-1 pointer-events-none border-l-2 border-indigo-400 bg-indigo-50 text-indigo-900 shadow-[-6px_0_16px_rgba(67,56,202,0.18)]"
          style={{
            width: edgePullHintWidth,
            opacity: Math.max(0.8, Math.min(1, edgePullPx / EDGE_NEXT_WEEK_THRESHOLD_PX)),
            transform: `translateX(${Math.max(0, edgePullHintWidth - edgePullPx)}px)`,
          }}
        >
          <HiChevronRight className="h-6 w-6 shrink-0 drop-shadow-sm" aria-hidden />
          <span className="whitespace-nowrap text-xs font-bold leading-none tracking-wide drop-shadow-sm">
            Next week
          </span>
        </div>
      )}

    </Card>
  );
}

export default LinearWeekViewDOM;